import React, { useId, useState, useEffect } from "react";
import {
  FaTimes,
  FaCheck,
  FaRocket,
  FaStar,
  FaUsers,
  FaRegClock,
} from "react-icons/fa";
import crtBg from "../../assets/images/course-items/crt-bg.jpg";
import cstBg from "../../assets/images/course-items/cst-bg.jpg";
import cttBg from "../../assets/images/course-items/ctt-bg.jpg";
import fullstackBg from "../../assets/images/course-items/fullstack-bg.png";
import softskillBg from "../../assets/images/course-items/softskill-bg.webp";
import spkengBg from "../../assets/images/course-items/spkeng-bg.jpg";
import bankingBg from "../../assets/images/course-items/banking-bg.png";
import { Button, Card, Container, IconTile, SectionHead } from "./ui";

const highlights = [
  {
    icon: FaRocket,
    title: "100% Placement Support",
    description:
      "Our comprehensive placement assistance ensures you land your dream job.",
  },
  {
    icon: FaUsers,
    title: "Expert Instructors",
    description:
      "Learn from industry professionals with years of real-world experience.",
  },
  {
    icon: FaStar,
    title: "Proven Track Record",
    description:
      "Join millions of successful students who have transformed their careers.",
  },
];

const SkillUpComponent = ({ skillUpComponentRef }) => {
  const [displayApplication, setDisplayApplication] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [jobApplication, setJobApplication] = useState({
    email: "",
    phone: "",
    name: "",
    course: "",
  });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    if (skillUpComponentRef.current) {
      observer.observe(skillUpComponentRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const courses = [
    {
      id: useId(),
      courseName: "Campus Recruitment Training (Quants, Reasoning & Verbal)",
      image: crtBg,
      duration: "3 Months",
      level: "Beginner to Advanced",
      features: ["Mock Tests", "Interview Prep", "Placement Support"],
    },
    {
      id: useId(),
      courseName: "Company Specific Training (Product Based and Service Based)",
      image: cstBg,
      duration: "2 Months",
      level: "Intermediate",
      features: ["Company-specific Prep", "Technical Rounds", "HR Training"],
    },
    {
      id: useId(),
      courseName: "Core Technical Training (C, C++, JAVA & PYTHON)",
      image: cttBg,
      duration: "4 Months",
      level: "Beginner to Advanced",
      features: ["Hands-on Projects", "Code Reviews", "Industry Mentors"],
    },
    {
      id: useId(),
      courseName: "Full Stack (JAVA & PYTHON)",
      image: fullstackBg,
      duration: "6 Months",
      level: "Intermediate to Advanced",
      features: ["Full Stack Projects", "Database Design", "Deployment"],
    },
    {
      id: useId(),
      courseName: "Soft Skills",
      image: softskillBg,
      duration: "1 Month",
      level: "All Levels",
      features: ["Communication", "Leadership", "Team Work"],
    },
    {
      id: useId(),
      courseName: "Spoken English",
      image: spkengBg,
      duration: "2 Months",
      level: "All Levels",
      features: ["Conversation Practice", "Accent Training", "Business English"],
    },
    {
      id: useId(),
      courseName: "Bank Exams (IBPS, SBI, RRB & RBI)",
      image: bankingBg,
      duration: "3 Months",
      level: "All Levels",
      features: ["Exam Strategy", "Mock Tests", "Current Affairs"],
    },
  ];

  const closeModal = () => {
    setDisplayApplication(false);
    setSelectedCourse(null);
    setJobApplication({ email: "", phone: "", name: "", course: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", jobApplication);
    closeModal();
  };

  const renderCourseCard = (item) => (
    <Card
      key={item.id}
      className={`flex flex-col overflow-hidden hover:-translate-y-1 hover:shadow-lg ${
        isVisible ? "animate-fade-in-up" : "opacity-0"
      }`}
    >
      {/* Thumbnail */}
      <div className="relative h-44 overflow-hidden bg-brand-50">
        <img
          src={item.image}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-brand backdrop-blur-sm">
          {item.level}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-base font-bold leading-snug text-ink">
          {item.courseName}
        </h3>

        <ul className="mt-4 flex-1 space-y-2">
          {item.features.map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <FaCheck className="h-3 w-3 flex-shrink-0 text-brand" />
              <span className="text-sm text-darkgrey">{feature}</span>
            </li>
          ))}
        </ul>

        {/* Meta row */}
        <div className="mt-5 flex items-center justify-between border-t border-brand-100 pt-4">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-darkgrey">
            <FaRegClock className="h-3.5 w-3.5 text-brand" />
            {item.duration}
          </span>
          <Button
            size="sm"
            variant="tint"
            onClick={() => {
              setSelectedCourse(item);
              setDisplayApplication(true);
            }}
          >
            Enroll Now
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <section
      ref={skillUpComponentRef}
      id="skillup"
      className="scroll-mt-24 bg-white py-20"
    >
      <Container>
        <SectionHead
          eyebrow="Skill Development"
          title="Skill up with us"
          subtitle="Join our classes for 100% placement support."
          action={
            <Button
              variant="outline"
              onClick={() =>
                window.open("https://app.smartica.co.in/en/courses/", "_blank")
              }
            >
              See All Courses
            </Button>
          }
        />

        {/* Intro copy */}
        <div className="mb-12 grid gap-6 rounded-3xl bg-brand-50 p-8 md:grid-cols-2">
          <p className="text-sm leading-relaxed text-darkgrey">
            At SMART, we offer a diverse range of courses designed to meet the
            needs of today's dynamic job market. Whether you're looking to
            advance your career, pivot to a new industry, or simply expand your
            skillset, we have the perfect program for you with 100% Placement
            support.
          </p>
          <p className="text-sm leading-relaxed text-darkgrey">
            Each course is led by industry experts with extensive experience and
            a passion for teaching. Our flexible learning options, including
            online, in-person, and hybrid formats, ensure that you can learn in a
            way that fits your schedule and learning style.
          </p>
        </div>

        {/* Highlights */}
        <div className="mb-14 grid gap-6 md:grid-cols-3">
          {highlights.map((item) => (
            <Card key={item.title} className="flex items-start gap-4 p-5">
              <IconTile icon={item.icon} />
              <div>
                <h3 className="mb-1 text-base font-bold text-ink">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-darkgrey">
                  {item.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Course grid */}
        <h3 className="mb-6 text-xl font-bold text-ink">Courses Offered</h3>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map(renderCourseCard)}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-3xl bg-brand p-8 text-center sm:p-12">
          <h3 className="text-2xl font-bold text-white sm:text-3xl">
            Ready to transform your career?
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">
            Join thousands of students who have successfully launched their
            careers with SMART Training
          </p>
          <Button
            variant="white"
            size="lg"
            className="mt-8"
            onClick={() =>
              window.open("https://app.smartica.co.in/en/courses/", "_blank")
            }
          >
            Explore All Courses
          </Button>
        </div>
      </Container>

      {/* Enrollment modal */}
      {displayApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-brand-100 p-6">
              <h2 className="text-lg font-bold text-ink">
                Enroll in {selectedCourse?.courseName}
              </h2>
              <button
                onClick={closeModal}
                aria-label="Close"
                className="rounded-full p-2 text-darkgrey transition-colors duration-300 hover:bg-brand-50"
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-6">
              {[
                { label: "Full Name", key: "name", type: "text" },
                { label: "Email Address", key: "email", type: "email" },
                { label: "Phone Number", key: "phone", type: "tel" },
              ].map((field) => (
                <div key={field.key}>
                  <label className="mb-2 block text-sm font-semibold text-ink">
                    {field.label} <span className="text-brand">*</span>
                  </label>
                  <input
                    type={field.type}
                    value={jobApplication[field.key]}
                    onChange={(e) =>
                      setJobApplication({
                        ...jobApplication,
                        [field.key]: e.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-brand-200 px-4 py-3 text-sm transition-all duration-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand"
                    required
                  />
                </div>
              ))}

              <div>
                <label className="mb-2 block text-sm font-semibold text-ink">
                  Course Duration
                </label>
                <div className="rounded-xl bg-brand-50 px-4 py-3 text-sm text-darkgrey">
                  {selectedCourse?.duration}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1">
                  Submit Application
                </Button>
                <Button type="button" variant="tint" onClick={closeModal}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default SkillUpComponent;
